// Mithril with the helpers of tutory/webapp's client/mithril.js (the parts this app needs):
//   m.m(tag, attrs, ...children)  extended attrs: cls, vars, onRender, onClick, disabledReason
//   m.withState(getView)          stateful closure components, called like functions
//   m.keyed(attrs, children)      keyed wrapper for components in keyed lists
//   m.onRemove(fn)                onremove hook as a child
//   m.t(key, ...)                translation, set in i18n.js
import cls from 'classies'
import m from 'mithril'

function callAll(...fns) {
  return (...args) => fns.forEach(fn => fn?.(...args))
}

/** The first reason whose condition holds; entries are [condition, reason]. */
function getDisabledReason(reasons) {
  for (const [condition, reason] of reasons || []) if (condition) return reason
  return null
}

function extendedM(tag, attrs, ...children) {
  const {
    cls: classes,
    vars,
    onRender,
    onClick,
    disabledReason,
    ...rest
  } = attrs
  if (classes)
    rest.className =
      cls({ ...classes, [rest.className]: rest.className }) || null
  if (vars) {
    const style = Object.entries(vars)
      .filter(([, value]) => value != null && value !== false)
      .map(([key, value]) => `${key}:${value}`)
    rest.style = [rest.style, ...style].filter(Boolean).join(';')
  }
  // called oncreate and onupdate
  if (onRender) {
    rest.oncreate = callAll(rest.oncreate, ...[onRender].flat())
    rest.onupdate = callAll(rest.onupdate, ...[onRender].flat())
  }
  if (onClick) rest.onclick = callAll(rest.onclick, ...[onClick].flat())
  // disabled with the reason as tooltip
  if (disabledReason) {
    const reason = getDisabledReason(disabledReason)
    if (reason || rest.disabled) {
      rest.disabled = true
      rest.title = reason || rest.title
    } else delete rest.disabled
  }
  return m(tag, rest, ...children)
}

function withState(getView) {
  const component = ({ attrs: { args: initArgs } }) => {
    const view = getView(...initArgs)
    return { view: ({ attrs: { args } }) => view(...args) }
  }
  return (...args) => m(component, { args })
}

m.m = extendedM
m.withState = withState
m.keyed = (attrs, children) => [m.fragment(attrs, children)]
m.onRemove = onremove => m.fragment({ onremove })

export default m
